import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { MonitorHeartOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';

const InsightsQualityPanel = () => {
  const { t } = useTranslation();
  const [quality, setQuality] = useState(null);
  const [error, setError] = useState('');

  const loadQuality = useCallback(async () => {
    try {
      const response = await makeApiCall('api/insights/admin/quality');
      if (!response.ok) throw new Error('quality_failed');
      setQuality(await response.json());
      setError('');
    } catch {
      setError(t('insights.quality.loadFailed'));
    }
  }, [t]);

  useEffect(() => {
    loadQuality();
  }, [loadQuality]);

  if (error) return <Alert severity="warning">{error}</Alert>;
  if (!quality) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }} aria-live="polite">
        <CircularProgress size={24} aria-label={t('insights.quality.loadingAria')} />
      </Box>
    );
  }

  const latestRollup = quality.rollup_runs?.[0];
  return (
    <Card variant="outlined" component="section" aria-labelledby="insights-quality-title">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <MonitorHeartOutlined sx={{ color: '#1565c0' }} aria-hidden="true" />
          <Typography id="insights-quality-title" variant="h6" component="h3">
            {t('insights.quality.title')}
          </Typography>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
          <Chip
            variant="outlined"
            label={t('insights.quality.predictions', {
              count: quality.predictions?.prediction_count || 0,
            })}
          />
          <Chip
            variant="outlined"
            label={t('insights.quality.evaluated', {
              count: quality.predictions?.evaluated || 0,
            })}
          />
          <Chip
            variant="outlined"
            label={t('insights.quality.inferences', {
              count: quality.inference?.inference_count || 0,
            })}
          />
          <Chip
            color={latestRollup?.status === 'succeeded' ? 'success' : 'warning'}
            variant="outlined"
            label={t('insights.quality.rollup', {
              status: latestRollup?.status || t('insights.notAvailable'),
            })}
          />
          <Chip
            variant="outlined"
            label={t('insights.quality.models', { count: quality.models?.length || 0 })}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

export default InsightsQualityPanel;
